import { useTranslation } from "react-i18next";

import { FileDropZone } from "./file-drop-zone";
import type { SelectedBpmnFile } from "./read-bpmn-file";

export type BpmnFileFieldProps = {
  readonly selected: SelectedBpmnFile | null;
  readonly onSelect: (_file: File) => void;
  readonly onClear: () => void;
};

const ISSUE_KEYS = {
  "not-bpmn": "processDesign.bpmnFile.notBpmn",
  unreadable: "processDesign.fileDrop.unreadable",
  "invalid-xml": "processDesign.bpmnFile.invalidXml",
  "no-executable-process": "processDesign.bpmnFile.noExecutableProcess",
  "multiple-executable-processes":
    "processDesign.bpmnFile.multipleExecutableProcesses",
} as const;

/**
 * .bpmn を 1 つ選ぶ欄。クリックで選ぶほか、ドロップもできる（枠は FileDropZone）。
 *
 * ファイルの読み取りと判定は呼び出し側（readBpmnFile）が行い、結果を selected で受ける。
 * ここは選んだ名前と使えない理由を文言にして枠へ渡す。
 */
export function BpmnFileField({
  selected,
  onSelect,
  onClear,
}: BpmnFileFieldProps) {
  const { t } = useTranslation("common");

  return (
    <FileDropZone
      label={t("processDesign.bpmnFile.label")}
      accept=".bpmn"
      hint={t("processDesign.bpmnFile.hint")}
      selectedName={selected === null ? null : selected.fileName}
      issue={issueMessage(selected, t)}
      onFiles={(files) => {
        const file = files[0];
        if (file !== undefined) onSelect(file);
      }}
      onClear={onClear}
    />
  );
}

/** 選んだファイルが使えない理由。使えるか未選択なら null。 */
function issueMessage(
  selected: SelectedBpmnFile | null,
  t: ReturnType<typeof useTranslation<"common">>["t"],
): string | null {
  if (selected === null || selected.meta.kind === "ok") return null;
  if (selected.meta.kind === "multiple-executable-processes") {
    return t(ISSUE_KEYS[selected.meta.kind], {
      ids: selected.meta.processIds.join(
        t("processDesign.bpmnFile.idSeparator"),
      ),
    });
  }
  return t(ISSUE_KEYS[selected.meta.kind]);
}
